import { moveElement } from "react-grid-layout/core";
import {
    applyItemChange,
    compactItems,
    fromGridItem,
    toGridItem,
} from "./geometry";
import { GRID_COLUMNS } from "./layoutConfig";
import type { SizeConstraints } from "./heights";
import type { OperationResult, SignalsDashboardLayoutV1 } from "./types";

export type MoveDirection = "left" | "right" | "up" | "down";

export type ResizeDirection = "narrower" | "wider" | "shorter" | "taller";

const MOVE_DELTAS: Record<MoveDirection, { x: number; y: number }> = {
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
    up: { x: 0, y: -1 },
    down: { x: 0, y: 1 },
};

const RESIZE_DELTAS: Record<
    ResizeDirection,
    { width: number; height: number }
> = {
    narrower: { width: -1, height: 0 },
    wider: { width: 1, height: 0 },
    shorter: { width: 0, height: -1 },
    taller: { width: 0, height: 1 },
};

/**
 * Keyboard move and resize, one cell per press. They run through the same
 * compaction as the pointer, so both paths produce the same layouts.
 */
export function moveItem(
    layout: SignalsDashboardLayoutV1,
    objectId: string,
    direction: MoveDirection,
): OperationResult {
    const item = layout.items.find((it) => it.objectId === objectId);
    if (!item) return { changed: false, layout, reason: "not-found" };

    const nextX = item.x + MOVE_DELTAS[direction].x;
    const nextY = item.y + MOVE_DELTAS[direction].y;

    if (nextX < 0 || nextX + item.width > GRID_COLUMNS || nextY < 0) {
        return { changed: false, layout, reason: "boundary" };
    }

    if (direction === "up" || direction === "down") {
        return moveVertically(layout, objectId, direction);
    }

    const nextLayout = applyItemChange(layout, {
        ...item,
        x: nextX,
        y: nextY,
    });
    const movedItem = nextLayout.items.find((it) => it.objectId === objectId);

    if (!movedItem || (movedItem.x === item.x && movedItem.y === item.y)) {
        return { changed: false, layout, reason: "collision" };
    }

    return { changed: true, layout: nextLayout };
}

/**
 * A one-row step into a stacked neighbour is undone by compaction, so the
 * card is pushed with react-grid-layout's own move, as a pointer drag is, one
 * row further at a time until it lands on the far side of its neighbour.
 */
function moveVertically(
    layout: SignalsDashboardLayoutV1,
    objectId: string,
    direction: "up" | "down",
): OperationResult {
    const item = layout.items.find((it) => it.objectId === objectId);
    if (!item) return { changed: false, layout, reason: "not-found" };

    const step = direction === "up" ? -1 : 1;
    const bottom = Math.max(...layout.items.map((it) => it.y + it.height));

    for (let y = item.y + step; y >= 0 && y <= bottom; y += step) {
        const gridItems = layout.items.map(toGridItem);
        const moving = gridItems.find((it) => it.i === objectId);
        if (!moving) break;

        const pushed = moveElement(
            gridItems,
            moving,
            item.x,
            y,
            true,
            false,
            "vertical",
            layout.columns,
        );
        const nextLayout = {
            ...layout,
            items: compactItems(pushed.map(fromGridItem), layout.columns),
        };
        const movedItem = nextLayout.items.find(
            (it) => it.objectId === objectId,
        );
        if (movedItem && (movedItem.y - item.y) * step > 0) {
            return { changed: true, layout: nextLayout };
        }
    }

    // Moving down into empty space is undone by compaction.
    return {
        changed: false,
        layout,
        reason: direction === "down" ? "vertical-compaction" : "collision",
    };
}

export function resizeItem(
    layout: SignalsDashboardLayoutV1,
    objectId: string,
    direction: ResizeDirection,
    constraints: SizeConstraints,
): OperationResult {
    const item = layout.items.find((it) => it.objectId === objectId);
    if (!item) return { changed: false, layout, reason: "not-found" };

    const nextWidth = item.width + RESIZE_DELTAS[direction].width;
    const nextHeight = item.height + RESIZE_DELTAS[direction].height;

    // Only the dimension being changed is checked. An auto-height card's
    // stored height can sit below its content floor (or above a maximum the
    // content pins), and that must not block a width step.
    const changesWidth = nextWidth !== item.width;
    if (
        changesWidth
            ? nextWidth < constraints.minWidth
            : nextHeight < constraints.minHeight
    ) {
        return { changed: false, layout, reason: "minimum-size" };
    }
    if (
        changesWidth
            ? nextWidth > constraints.maxWidth
            : constraints.maxHeight !== undefined &&
              nextHeight > constraints.maxHeight
    ) {
        return { changed: false, layout, reason: "maximum-size" };
    }
    if (item.x + nextWidth > GRID_COLUMNS) {
        return { changed: false, layout, reason: "boundary" };
    }

    const nextLayout = applyItemChange(layout, {
        ...item,
        width: nextWidth,
        height: nextHeight,
    });
    const resizedItem = nextLayout.items.find((it) => it.objectId === objectId);

    if (
        !resizedItem ||
        (resizedItem.width === item.width && resizedItem.height === item.height)
    ) {
        return { changed: false, layout, reason: "collision" };
    }

    return { changed: true, layout: nextLayout };
}
